import { useEffect, useRef, useState } from "react";

type MathJaxApi = {
  typesetPromise?: (elements?: Element[]) => Promise<void>;
};

type MathJaxWindow = Window & {
  MathJax?: MathJaxApi;
};

type MathJaxFormulaProps = {
  formula: string;
  fallback: string;
};

const measurementFontSize = 16;
const minimumFontSize = 6;
const maximumFontSize = 32;
const horizontalBreathingRoom = 12;

export function MathJaxFormula({ formula, fallback }: MathJaxFormulaProps) {
  const formulaRef = useRef<HTMLSpanElement>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let attempts = 0;
    let timer: number | undefined;

    const waitForMathJax = () => {
      const mathJax = (window as MathJaxWindow).MathJax;

      if (mathJax?.typesetPromise) {
        setIsReady(true);
        return;
      }

      attempts += 1;
      if (attempts < 100) {
        timer = window.setTimeout(waitForMathJax, 100);
      }
    };

    waitForMathJax();

    return () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    if (!isReady || !formulaRef.current) {
      return;
    }

    const formulaElement = formulaRef.current;
    const mathJax = (window as MathJaxWindow).MathJax;
    let animationFrame: number | undefined;
    let resizeObserver: ResizeObserver | undefined;
    let observedWidth = formulaElement.clientWidth;
    let disposed = false;

    const fitFormulaToWidth = () => {
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }

      formulaElement.style.setProperty(
        "--league-formula-font-size",
        `${measurementFontSize}px`
      );

      animationFrame = window.requestAnimationFrame(() => {
        const renderedFormula = formulaElement.querySelector("mjx-container");
        const renderedWidth =
          renderedFormula?.getBoundingClientRect().width ?? 0;
        const availableWidth = Math.max(
          formulaElement.clientWidth - horizontalBreathingRoom,
          0
        );

        if (disposed || renderedWidth <= 0 || availableWidth <= 0) {
          return;
        }

        const fittedFontSize = Math.min(
          maximumFontSize,
          Math.max(
            minimumFontSize,
            measurementFontSize * (availableWidth / renderedWidth)
          )
        );

        formulaElement.style.setProperty(
          "--league-formula-font-size",
          `${fittedFontSize.toFixed(2)}px`
        );
      });
    };

    void mathJax
      ?.typesetPromise?.([formulaElement])
      .then(() => {
        if (disposed) {
          return;
        }

        fitFormulaToWidth();

        if (typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(([entry]) => {
            const nextWidth = entry.contentRect.width;
            if (Math.abs(nextWidth - observedWidth) < 0.5) {
              return;
            }

            observedWidth = nextWidth;
            fitFormulaToWidth();
          });
          resizeObserver.observe(formulaElement);
        } else {
          window.addEventListener("resize", fitFormulaToWidth);
        }
      })
      .catch(() => undefined);

    return () => {
      disposed = true;
      resizeObserver?.disconnect();
      window.removeEventListener("resize", fitFormulaToWidth);
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [formula, isReady]);

  return (
    <span
      ref={formulaRef}
      className="league-formula-math"
      aria-label="Score = 100000 × r の -0.75 乗 × log10(1 + log10 N)"
    >
      {isReady ? formula : fallback}
    </span>
  );
}
